import { getAnalytics, logEvent } from "firebase/analytics";

// Tracking-page analytics funnel (Firebase Analytics / GA4).
//
// Every event is named tp_<name> and carries the same context (awb, brand, status,
// entry source, device, build) so the funnel can be sliced the same way everywhere.
// Events reach Firebase only when REACT_APP_REALM is "prod". In every other realm
// they are kept on window.__tpEvents (for tests) and, with localStorage tp:debug=1,
// printed to the console.
//
// GA4 limits: 25 parameters per event, parameter values up to 100 characters.

const REALM = String(process.env.REACT_APP_REALM || "").trim().toLowerCase();
export const ANALYTICS_ENABLED = REALM === "prod";
const BUILD = String(process.env.REACT_APP_BUILD || "local").slice(0, 20);

const ENTRY_KEY = "tp:entry";
const VISITS_KEY = "tp:visits";

const context = {};
let analytics = null;

const safe = (fn, fallback) => {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
};

const read = (storage, key) => safe(() => JSON.parse(window[storage].getItem(key)), null);
const write = (storage, key, value) => safe(() => window[storage].setItem(key, JSON.stringify(value)));

const decodeBase64 = (value) => safe(() => window.atob(String(value)), null);

// Where this session came from: the SMS / WhatsApp / email link's query params,
// the referrer, or the home page search. Fixed once per browser session so every
// page of the visit carries the same value.
const computeEntry = () => {
  const params = new URLSearchParams(window.location.search);
  const source = params.get("source");
  const templateId = params.get("templateId");
  const entry = { template_id: templateId ? (decodeBase64(templateId) || templateId).slice(0, 60) : undefined };
  if (source) {
    const decoded = (decodeBase64(source) || source).trim().toLowerCase();
    entry.entry_source = ["whatsapp", "sms", "email"].includes(decoded) ? decoded : `other:${decoded.slice(0, 30)}`;
  } else if (templateId) {
    entry.entry_source = "template_link";
  } else {
    const ref = safe(() => (document.referrer ? new URL(document.referrer).host : ""), "");
    if (!ref) entry.entry_source = "direct"; // app and SMS link clicks carry no referrer
    else if (ref === window.location.host) entry.entry_source = "internal";
    else {
      entry.entry_source = "referral";
      entry.referrer_host = ref.slice(0, 60);
    }
  }
  return entry;
};

const getEntry = () => {
  const saved = read("sessionStorage", ENTRY_KEY);
  if (saved) return saved;
  const entry = computeEntry();
  write("sessionStorage", ENTRY_KEY, entry);
  return entry;
};

// The home page search is an entry of its own (the session starts on "/").
export const setEntrySource = (entry_source) => write("sessionStorage", ENTRY_KEY, { ...getEntry(), entry_source });

const device = () =>
  safe(() => (window.matchMedia("(max-width: 767px)").matches || /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop"), "unknown");

const hasOtpSession = () => safe(() => (sessionStorage.getItem("validateotp") ? 1 : 0), 0);

const base = () => ({ ...getEntry(), device: device(), has_otp_session: hasOtpSession(), build: BUILD });

// Firebase accepts strings and numbers; arrays join, booleans become 1/0, empties drop.
const clean = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    let v = value;
    if (Array.isArray(v)) v = v.join(",");
    if (typeof v === "boolean") v = v ? 1 : 0;
    if (typeof v === "number" && !Number.isFinite(v)) return;
    if (typeof v === "object") v = safe(() => JSON.stringify(v), String(v));
    if (typeof v === "string") v = v.slice(0, 100);
    out[key] = v;
  });
  return out;
};

const debugEnabled = () => process.env.NODE_ENV === "development" || safe(() => localStorage.getItem("tp:debug") === "1", false);

const firebase = () => {
  if (!analytics) analytics = safe(() => getAnalytics(), null);
  return analytics;
};

/** Context sent with every event (awb, org_id, shipper_id, shipment_status, tone). */
export const setContext = (patch) => {
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") delete context[key];
    else context[key] = value;
  });
};

export const getContext = () => ({ ...context });

/** Send one event. Names are tp_<name>; params are flat. */
export const track = (name, params = {}) => {
  const payload = clean({ ...base(), ...context, ...params });
  if (!ANALYTICS_ENABLED) {
    safe(() => {
      window.__tpEvents = window.__tpEvents || [];
      window.__tpEvents.push({ name, params: payload, at: Date.now() });
      if (debugEnabled()) console.debug(`[tp] ${name}`, payload); // eslint-disable-line no-console
    });
    return;
  }
  const a = firebase();
  if (a) safe(() => logEvent(a, name, payload));
};

/** tp_error: area = page_load | full_details | radar | otp_send | call_session | rating_submit | ... */
export const trackError = (area, response, extra = {}) => track("tp_error", { area, code: errorCode(response), ...extra });

/** A response code for events: the API's code, "network" when the call never answered. */
export const errorCode = (response) => {
  if (response === undefined || response === null) return "network";
  if (typeof response === "string") return response.slice(0, 40);
  const code = response.code ?? response.status ?? response.message;
  return code === undefined || code === null ? "other" : String(code).slice(0, 40);
};

/** Milliseconds since the timer was made. */
export const timer = () => {
  const start = Date.now();
  return () => Date.now() - start;
};

// Visits per AWB on this device (localStorage): visit_number and minutes since the last one.
export const recordVisit = (awb) => {
  if (!awb) return {};
  const visits = read("localStorage", VISITS_KEY) || {};
  const key = String(awb).toUpperCase();
  const prev = visits[key];
  const now = Date.now();
  const entry = { n: (prev?.n || 0) + 1, last: now };
  // Keep the map small: the 50 most recent AWBs.
  const next = Object.fromEntries(
    Object.entries({ ...visits, [key]: entry })
      .sort((a, b) => b[1].last - a[1].last)
      .slice(0, 50)
  );
  write("localStorage", VISITS_KEY, next);
  return {
    visit_number: entry.n,
    minutes_since_last_visit: prev?.last ? Math.round((now - prev.last) / 60000) : undefined,
  };
};

// ---- Vocabularies shared with operations ----

// Rider failed-delivery remarks (logistic GeneralConstants). ":Verified" means the
// rider verified the attempt with the buyer's OTP.
const REMARK_CLASSES = [
  ["customer not answering", "customer_not_answering"],
  ["customer not available", "customer_not_available"],
  ["day after tomorrow", "reschedule_day_after"],
  ["reschedule for tomorrow", "reschedule_tomorrow"],
  ["future date", "reschedule_future"],
  ["switched off", "phone_unreachable"],
  ["not reachable", "phone_unreachable"],
  ["phone number is wrong", "phone_wrong"],
  ["incomplete address", "wrong_address"],
  ["wrong address", "wrong_address"],
  ["wrong pin", "wrong_pin"],
  ["operational constraint", "ops_constraint"],
  ["refused to accept", "customer_refused"],
  ["cod amount not ready", "cod_not_ready"],
  ["fake", "fake_attempt"],
];

export const classifyRemark = (remark) => {
  const text = String(remark || "").toLowerCase();
  if (!text) return {};
  const match = REMARK_CLASSES.find(([needle]) => text.includes(needle));
  return { ndr_remark_class: match ? match[1] : "other", remark_verified: text.includes(":verified") ? 1 : 0 };
};

// Buyer report reasons → the support ticket subtypes operations use (ticket_type, Last Mile).
export const REPORT_REASON_CLASS = {
  "Misbehaviour/Misconduct": "rider_misbehaviour",
  "No pre-delivery contact": "fake_attempt",
  "Refused doorstep delivery": "rider_misbehaviour",
  "Paid, but undelivered": "fake_delivery",
  "Other Reasons": "others",
};

// Rating tags → fixed codes.
export const RATING_TAG_CODE = {
  "On time": "on_time",
  "Polite Kaptaan": "polite",
  "Safe packaging": "safe_packaging",
  "Followed instructions": "followed_instructions",
  "Called before arriving": "called_before",
  "Late delivery": "late",
  "Damaged package": "damaged",
  "Rude behaviour": "rude",
  "Did not call": "no_call",
  "Wrong location": "wrong_location",
};

// OTP modal `action` prop → the CTA that opened it.
export const OTP_TRIGGER = {
  Edit: "edit",
  Edit2: "show_details",
  cancel: "cancel",
  requestotp: "show_otp",
  showProduct: "show_items",
  resheduleTab: "tab_switch",
};
